import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

// Buttons live inside the iMIS reset (see index.css), so every visual style is
// explicit here. `type="button"` is forced to avoid WebForms postbacks.
export const buttonVariants = cva(
  "agd:inline-flex agd:items-center agd:justify-center agd:gap-2 agd:rounded-md agd:font-semibold agd:transition-opacity agd:cursor-pointer disabled:agd:opacity-50 disabled:agd:cursor-not-allowed hover:not-disabled:agd:opacity-88",
  {
    variants: {
      variant: {
        /* Blue — "Add to Cart", View Cart. */
        primary: "agd:bg-primary agd:text-primary-foreground",
        /* Orange — the coupon CTA. */
        accent: "agd:bg-accent agd:text-white",
        /* Blue outline — secondary header actions. */
        outline:
          "agd:bg-transparent agd:border agd:border-primary agd:text-primary hover:not-disabled:agd:bg-primary hover:not-disabled:agd:text-primary-foreground hover:not-disabled:agd:opacity-100",
        /* Muted — already-purchased / disabled affordance. */
        muted: "agd:bg-muted agd:text-muted-foreground",
        /* Neutral cancel. */
        ghost: "agd:bg-[#edf2f7] agd:text-[#4a5568]",
      },
      size: {
        default: "agd:px-4 agd:py-2.5 agd:text-sm",
        sm: "agd:px-4 agd:py-2 agd:text-sm",
        block: "agd:w-full agd:px-4 agd:py-2.5 agd:text-sm",
      },
    },
    defaultVariants: { variant: "primary", size: "default" },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export default function Button({ className, variant, size, ...props }: ButtonProps) {
  return <button type="button" className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}
